import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { CalendarIcon, MapPin, Truck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logListingEvent } from "@/lib/listingEvents";

interface PurchaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listing: {
    id: string;
    title: string;
    price: number;
    price_unit: string | null;
    quantity: number | null;
    seller_id: string;
    pickup_enabled: boolean;
    shipping_enabled: boolean;
    shipping_price: number | null;
    delivery_radius_km: number | null;
    location: string | null;
    municipality: string | null;
  };
  buyerId: string;
}

function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

async function geocode(address: string): Promise<{ lat: number; lon: number } | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=se&q=${encodeURIComponent(address)}`,
      { headers: { "User-Agent": "Barka/1.0" } }
    );
    const data = await res.json();
    if (data.length > 0) {
      return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
    }
  } catch {}
  return null;
}

export function PurchaseModal({ open, onOpenChange, listing, buyerId }: PurchaseModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [deliveryMethod, setDeliveryMethod] = useState<"pickup" | "shipping" | null>(
    listing.pickup_enabled && !listing.shipping_enabled ? "pickup" :
    !listing.pickup_enabled && listing.shipping_enabled ? "shipping" : null
  );
  const [pickupDate, setPickupDate] = useState<Date | undefined>();
  const [pickupTime, setPickupTime] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [checkingAddress, setCheckingAddress] = useState(false);
  const [addressValid, setAddressValid] = useState(false);
  const [suggestions, setSuggestions] = useState<{ label: string; full: string }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.trim().length < 3) { setSuggestions([]); return; }
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=se&q=${encodeURIComponent(query)}`,
        { headers: { "User-Agent": "Barka/1.0" } }
      );
      const data = await res.json();
      const mapped = data.map((d: any) => {
        const a = d.address || {};
        const street = [a.road, a.house_number].filter(Boolean).join(" ");
        const city = a.city || a.town || a.village || a.municipality || "";
        const parts = [street, a.postcode, city].filter(Boolean);
        return { label: parts.join(", ") || d.display_name, full: d.display_name };
      });
      setSuggestions(mapped);
      setShowSuggestions(mapped.length > 0);
    } catch {
      setSuggestions([]);
    }
  }, []);

  const checkAddress = useCallback(async (address: string) => {
    if (!address.trim() || !listing.delivery_radius_km) {
      setAddressError(null);
      setAddressValid(address.trim().length > 0);
      setCheckingAddress(false);
      return;
    }

    setCheckingAddress(true);
    setAddressError(null);
    setAddressValid(false);

    const sellerLocation = listing.location || listing.municipality;
    if (!sellerLocation) {
      setAddressValid(true);
      setCheckingAddress(false);
      return;
    }

    const [buyerCoords, sellerCoords] = await Promise.all([
      geocode(address),
      geocode(sellerLocation + ", Sverige"),
    ]);

    setCheckingAddress(false);

    if (!buyerCoords || !sellerCoords) {
      setAddressValid(true);
      return;
    }

    const dist = haversineDistance(sellerCoords.lat, sellerCoords.lon, buyerCoords.lat, buyerCoords.lon);
    if (dist > listing.delivery_radius_km) {
      setAddressError(`Din adress ligger utanför säljarens leveransområde. Maximalt avstånd är ${listing.delivery_radius_km} km.`);
      setAddressValid(false);
    } else {
      setAddressValid(true);
    }
  }, [listing.delivery_radius_km, listing.location, listing.municipality]);

  const handleAddressChange = (value: string) => {
    setShippingAddress(value);
    setAddressError(null);
    setAddressValid(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
    if (value.trim().length > 2) {
      suggestDebounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion: { label: string; full: string }) => {
    setShippingAddress(suggestion.label);
    setShowSuggestions(false);
    setSuggestions([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
    checkAddress(suggestion.full);
  };

  const maxQty = listing.quantity ?? 999;
  const unitPrice = listing.price;
  const shippingCost = deliveryMethod === "shipping" && listing.shipping_price ? listing.shipping_price : 0;
  const totalPrice = unitPrice * quantity + shippingCost;

  const shippingFieldValid = deliveryMethod === "shipping"
    ? (listing.delivery_radius_km ? addressValid : shippingAddress.trim().length > 0)
    : false;

  const isValid = quantity >= 1 && quantity <= maxQty && deliveryMethod !== null && !checkingAddress && (
    (deliveryMethod === "pickup" && pickupDate && pickupTime) ||
    (deliveryMethod === "shipping" && shippingFieldValid)
  );

  const handleSubmit = async () => {
    if (!isValid) return;
    setLoading(true);

    const { error } = await supabase.from("purchase_requests").insert({
      listing_id: listing.id,
      buyer_id: buyerId,
      seller_id: listing.seller_id,
      quantity,
      total_price: totalPrice,
      delivery_method: deliveryMethod!,
      pickup_date: deliveryMethod === "pickup" && pickupDate ? format(pickupDate, "yyyy-MM-dd") : null,
      pickup_time: deliveryMethod === "pickup" ? pickupTime : null,
      shipping_address: deliveryMethod === "shipping" ? shippingAddress : null,
    });

    setLoading(false);
    if (error) {
      toast.error("Kunde inte skicka köpförslag: " + error.message);
    } else {
      toast.success("Ditt köpförslag har skickats!");
      logListingEvent(listing.id, "purchase_request");
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Köp – {listing.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Quantity */}
          <div>
            <Label>Antal (max {maxQty})</Label>
            <Input
              type="number"
              min={1}
              max={maxQty}
              value={quantity}
              onChange={(e) => setQuantity(Math.min(maxQty, Math.max(1, parseInt(e.target.value) || 1)))}
            />
          </div>

          {/* Delivery method */}
          <div>
            <Label className="mb-2 block">Leveransmetod</Label>
            <div className="grid gap-3">
              {listing.pickup_enabled && (
                <button
                  onClick={() => setDeliveryMethod("pickup")}
                  className={cn(
                    "flex items-center gap-3 rounded-lg border p-4 text-left transition-colors",
                    deliveryMethod === "pickup"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <MapPin className="h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Hämta hos säljaren</p>
                    <p className="text-xs text-muted-foreground">Välj datum och tid för upphämtning</p>
                  </div>
                </button>
              )}
              {listing.shipping_enabled && (
                <button
                  onClick={() => setDeliveryMethod("shipping")}
                  className={cn(
                    "flex items-center gap-3 rounded-lg border p-4 text-left transition-colors",
                    deliveryMethod === "shipping"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <Truck className="h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Frakt</p>
                    <p className="text-xs text-muted-foreground">
                      {listing.shipping_price ? `${listing.shipping_price} kr fraktkostnad` : "Ange leveransadress"}
                      {listing.delivery_radius_km ? ` • Max ${listing.delivery_radius_km} km` : ""}
                    </p>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Pickup fields */}
          {deliveryMethod === "pickup" && (
            <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
              <div>
                <Label>Datum</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-full justify-start text-left font-normal", !pickupDate && "text-muted-foreground")}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {pickupDate ? format(pickupDate, "PPP", { locale: sv }) : "Välj datum"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[1100]" align="start">
                    <Calendar
                      mode="single"
                      selected={pickupDate}
                      onSelect={setPickupDate}
                      disabled={(date) => date < new Date()}
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Tid</Label>
                <Input
                  type="time"
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Shipping fields */}
          {deliveryMethod === "shipping" && (
            <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
              <div className="relative">
                <Label>Leveransadress</Label>
                <Input
                  ref={inputRef}
                  value={shippingAddress}
                  onChange={(e) => handleAddressChange(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  placeholder="Gatuadress, postnummer, ort"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div
                    ref={suggestionsRef}
                    className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-border bg-popover shadow-md"
                  >
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                        onClick={() => handleSelectSuggestion(s)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
                {checkingAddress && (
                  <p className="text-xs text-muted-foreground mt-1">Kontrollerar adress…</p>
                )}
                {addressError && (
                  <p className="text-xs text-destructive mt-1">{addressError}</p>
                )}
              </div>
            </div>
          )}

          {/* Total */}
          <div className="rounded-lg border border-border bg-card p-4 text-center">
            <p className="text-sm text-muted-foreground">Totalt</p>
            <p className="text-2xl font-bold text-foreground">{totalPrice.toLocaleString("sv-SE")} kr</p>
            {shippingCost > 0 && (
              <p className="text-xs text-muted-foreground">inkl. {shippingCost} kr frakt</p>
            )}
          </div>

          <Button
            className="w-full bg-primary hover:bg-primary/90"
            disabled={!isValid || loading}
            onClick={handleSubmit}
          >
            {loading ? "Skickar…" : "Skicka köpförslag"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
